import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * ClasificacionInput
 * ─────────────────────────────────────────────────────────────────────────────
 * Combo de texto libre con búsqueda: a diferencia de SearchSelect (que exige
 * elegir una opción existente por id), acá el valor ES el texto — el usuario
 * puede escribir una clasificación nueva o elegir una ya usada en el
 * condominio desde el desplegable. Sin esto, dos cuentas de la misma
 * clasificación terminarían con "Cuentas por Cobrar Comerciales" y "cuentas
 * por cobrar comerciales" como grupos distintos en los reportes.
 *
 * Props:
 *   value       → string
 *   onChange    → fn(string)
 *   options     → string[] — clasificaciones ya usadas en el condominio
 *   placeholder → string
 */
export function ClasificacionInput({ value, onChange, options = [], placeholder }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const anchorRef   = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handler(e) {
      const insideAnchor   = anchorRef.current?.contains(e.target);
      const insideDropdown = dropdownRef.current?.contains(e.target);
      if (!insideAnchor && !insideDropdown) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    function updateRect() {
      if (anchorRef.current) setRect(anchorRef.current.getBoundingClientRect());
    }
    updateRect();
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [open]);

  const q = (value || "").trim().toLowerCase();
  const opcionesUnicas = Array.from(new Set(options.filter(Boolean)));
  const filtradas = opcionesUnicas.filter(
    (o) => o.toLowerCase() !== q && (!q || o.toLowerCase().includes(q))
  );

  let dropdownTop = 0, dropdownLeft = 0, dropdownWidth = 0;
  if (rect) {
    dropdownTop = rect.bottom + 4;
    dropdownWidth = Math.max(rect.width, 260);
    dropdownLeft = Math.max(8, Math.min(rect.left, window.innerWidth - dropdownWidth - 8));
  }

  return (
    <div ref={anchorRef} style={{ position: "relative" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        background: "var(--bg-input)", border: "1px solid var(--border-input)",
        borderRadius: 7, padding: "9px 12px",
      }}>
        <i className="fa-solid fa-magnifying-glass"
          style={{ color: "var(--text-muted)", fontSize: "0.75rem" }} />
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          style={{
            flex: 1, background: "none", border: "none", outline: "none",
            color: "var(--text-primary)", fontSize: "0.87rem",
          }}
        />
      </div>

      {open && rect && filtradas.length > 0 && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "fixed", top: dropdownTop, left: dropdownLeft, width: dropdownWidth,
            background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 8, zIndex: 10050, overflow: "hidden",
            maxHeight: 220, overflowY: "auto",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
          }}
        >
          {filtradas.map((op) => (
            <button
              key={op}
              type="button"
              onClick={() => { onChange(op); setOpen(false); }}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "8px 12px", background: "transparent", border: "none",
                borderBottom: "1px solid var(--border-card)", cursor: "pointer",
                color: "var(--text-primary)", fontSize: "0.83rem",
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = "var(--hover-bg-strong)"; }}
              onMouseOut={(e)  => { e.currentTarget.style.background = "transparent"; }}
            >
              {op}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
